import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import type { PrDraftBody, PrFileBody, PrFormBody, PrSavedBody } from '../../shared/pr/api';
import {
  LINE_TYPES,
  MAX_FILE_BYTES,
  MAX_FILES,
  MAX_LINES,
  UNITS,
  missingForSubmit,
  type LineType,
  type PrInput,
  type Unit,
} from '../../shared/pr/form';
import type { RenewalDraft } from '../../shared/contracts/contracts';
import { DateHint } from '../components/DateHint';
import { useI18n, type MessageKey } from '../i18n';
import { ApiError, apiGet, apiSend, apiUpload } from '../lib/api';

// New purchase request (screen 02, P1.2). The profile is shown read-only; the requester picks the
// cost centre charged (D105); entity, office and currency are worked out by the broker. Files go up once the request has a number.

interface LineDraft {
  key: number;
  description: string;
  specs: string;
  qty: string;
  unit: Unit;
  lineType: LineType;
}

interface FormState {
  title: string;
  /** Cost centre charged; '' means the requester's own vertical (D105). */
  chargedTo: string;
  category: string;
  location: string;
  neededBy: string;
  justification: string;
  estBudget: string;
  rush: boolean;
  rushReason: string;
  lines: LineDraft[];
}

type Load = { kind: 'loading' } | { kind: 'error' } | { kind: 'ready'; options: PrFormBody };

type Done = { kind: 'submitted' | 'draft'; prNo: string } | null;

let nextKey = 1;
const newLine = (): LineDraft => ({
  key: nextKey++,
  description: '',
  specs: '',
  qty: '1',
  unit: 'piece',
  lineType: 'one-off',
});

const EMPTY: FormState = {
  title: '',
  chargedTo: '',
  category: '',
  location: '',
  neededBy: '',
  justification: '',
  estBudget: '',
  rush: false,
  rushReason: '',
  lines: [newLine()],
};

function fromInput(input: PrInput): FormState {
  return {
    title: input.title,
    chargedTo: input.chargedTo ?? '',
    category: input.category ?? '',
    location: input.location ?? '',
    neededBy: input.neededBy ?? '',
    justification: input.justification ?? '',
    estBudget: input.estBudget == null ? '' : String(input.estBudget),
    rush: input.rush === true,
    rushReason: input.rushReason ?? '',
    lines: input.lines?.length
      ? input.lines.map((l) => ({
          key: nextKey++,
          description: l.description,
          specs: l.specs ?? '',
          qty: String(l.qty),
          unit: l.unit,
          lineType: l.lineType,
        }))
      : [newLine()],
  };
}

/** Blank item rows are dropped; everything else goes to the broker as typed. */
function toInput(f: FormState): PrInput {
  const lines = f.lines
    .filter((l) => l.description.trim() || l.specs.trim())
    .map((l) => ({
      description: l.description.trim(),
      ...(l.specs.trim() ? { specs: l.specs.trim() } : {}),
      qty: Number(l.qty),
      unit: l.unit,
      lineType: l.lineType,
    }));
  return {
    title: f.title.trim(),
    ...(f.chargedTo ? { chargedTo: f.chargedTo } : {}),
    category: f.category,
    location: f.location,
    neededBy: f.neededBy,
    justification: f.justification.trim(),
    estBudget: f.estBudget.trim() === '' ? null : Number(f.estBudget),
    rush: f.rush,
    rushReason: f.rush ? f.rushReason.trim() : '',
    lines,
  };
}

const ERROR_CODES = new Set([
  'invalid_input',
  'profile_gate',
  'conflict',
  'not_editable',
  'not_found',
  'not_configured',
  'too_large',
  'data_unavailable',
]);

function errorKey(code: string): MessageKey {
  return (ERROR_CODES.has(code) ? `pr.error.${code}` : 'pr.error.generic') as MessageKey;
}

/** Broker field paths look like `pr.lines.0.qty`; the form keys them as `lines.0.qty`. */
function fieldErrors(err: ApiError): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(err.fields)) out[k.replace(/^pr\./, '')] = v;
  return out;
}

export function NewRequestPage({
  draftNo,
  renewOf,
}: {
  draftNo?: string | undefined;
  /** A contract to renew (P3.5): the form starts prefilled from it. */
  renewOf?: string | undefined;
}) {
  const { t, lang } = useI18n();
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [form, setForm] = useState<FormState>(EMPTY);
  const [prNo, setPrNo] = useState<string | null>(draftNo ?? null);
  const [etag, setEtag] = useState<string | null>(null);
  const [savedFiles, setSavedFiles] = useState<string[]>([]);
  const [pending, setPending] = useState<File[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState<Done>(null);

  useEffect(() => {
    let live = true;
    (async () => {
      const options = await apiGet<PrFormBody>('/api/prs/form');
      if (draftNo) {
        const draft = await apiGet<PrDraftBody>(`/api/prs/${encodeURIComponent(draftNo)}`);
        if (!live) return;
        setForm(fromInput(draft.input));
        setEtag(draft.etag);
        setSavedFiles(draft.files);
      }
      if (renewOf && !draftNo) {
        const r = await apiGet<RenewalDraft>(
          `/api/contracts/${encodeURIComponent(renewOf)}/renewal`,
        );
        if (!live) return;
        setForm(
          fromInput({
            title: r.title,
            justification: r.justification,
            estBudget: r.estBudget,
            lines: r.lines,
          }),
        );
      }
      if (live) setLoad({ kind: 'ready', options });
    })().catch(() => live && setLoad({ kind: 'error' }));
    return () => {
      live = false;
    };
  }, [draftNo, renewOf]);

  if (load.kind === 'loading') {
    return (
      <section class="card" aria-busy="true">
        <p>{t('pr.loading')}</p>
      </section>
    );
  }
  if (load.kind === 'error') {
    return (
      <section class="card card--error" role="alert">
        <p>{t('pr.loadError')}</p>
      </section>
    );
  }
  if (done) {
    return (
      <section class="card" role="status">
        <h2 class="card__title">
          {done.kind === 'submitted'
            ? t('pr.done.title', { no: done.prNo })
            : t('pr.draft.saved', { no: done.prNo })}
        </h2>
        {done.kind === 'submitted' && <p>{t('pr.done.body')}</p>}
        <a class="button" href="#/">
          {t('pr.backHome')}
        </a>
      </section>
    );
  }

  const { requester, locations, categories, verticals } = load.options;
  const chargedTo = form.chargedTo || requester.vertical;
  const chargedEntity =
    verticals.find((v) => v.vertical === chargedTo)?.entity ?? requester.entity ?? '';
  const currency = locations.find((l) => l.location === form.location)?.currency ?? '';
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm({ ...form, [key]: value });
  const setLine = (key: number, patch: Partial<LineDraft>) =>
    set(
      'lines',
      form.lines.map((l) => (l.key === key ? { ...l, ...patch } : l)),
    );
  const message = (k: string): string | undefined => {
    const v = errors[k];
    if (!v) return undefined;
    return v === 'Required' ? t('pr.err.required') : lang === 'en' ? v : t('pr.err.check');
  };

  async function save(submit: boolean) {
    const input = toInput(form);
    const missing = {
      ...(submit ? missingForSubmit(input) : {}),
      ...(input.title ? {} : { title: 'Required' }),
    };
    if (Object.keys(missing).length) {
      setErrors(missing);
      setProblem(t('pr.error.invalid_input'));
      return;
    }
    setSaving(true);
    setErrors({});
    setProblem(null);
    try {
      let no = prNo;
      let tag = etag;
      // Files need a request number first, so a request with files is saved as a draft,
      // the files go up, and only then is it submitted.
      if (!no) {
        const created = await apiSend<PrSavedBody>('POST', '/api/prs', {
          submit: submit && pending.length === 0,
          pr: input,
          ...(renewOf ? { renewalOf: renewOf } : {}),
        });
        no = created.prNo;
        tag = created.etag;
        setPrNo(no);
        setEtag(tag);
        if (created.status !== 'Draft') {
          setDone({ kind: 'submitted', prNo: no });
          return;
        }
        // Keep the address pointing at the draft without re-running the route (uploads follow).
        history.replaceState(null, '', `#/requests/draft/${no}`);
      } else {
        const saved = await apiSend<PrSavedBody>('PATCH', `/api/prs/${encodeURIComponent(no)}`, {
          etag: tag,
          submit: submit && pending.length === 0,
          pr: input,
        });
        tag = saved.etag;
        setEtag(tag);
        if (saved.status !== 'Draft') {
          setDone({ kind: 'submitted', prNo: no });
          return;
        }
      }
      const uploaded: string[] = [];
      for (const file of pending) {
        try {
          const res = await apiUpload<PrFileBody>(`/api/prs/${encodeURIComponent(no)}/files`, file);
          uploaded.push(res.name);
        } catch (err) {
          setSavedFiles((f) => [...f, ...uploaded]);
          setPending((p) => p.filter((f) => !uploaded.includes(f.name) && f !== file));
          const reason =
            err instanceof ApiError
              ? (err.fields.file ?? t(errorKey(err.code)))
              : t('pr.error.generic');
          setProblem(t('pr.error.file', { name: file.name, reason }));
          return;
        }
      }
      setSavedFiles((f) => [...f, ...uploaded]);
      setPending([]);
      if (submit && pending.length > 0) {
        const saved = await apiSend<PrSavedBody>('PATCH', `/api/prs/${encodeURIComponent(no)}`, {
          etag: tag,
          submit: true,
          pr: input,
        });
        setEtag(saved.etag);
        setDone({ kind: 'submitted', prNo: no });
        return;
      }
      if (!submit) setDone({ kind: 'draft', prNo: no });
    } catch (err) {
      const apiErr = err instanceof ApiError ? err : null;
      const ref = apiErr?.correlationId;
      setProblem(
        t(errorKey(apiErr?.code ?? 'generic')) + (ref ? ` ${t('error.reference', { ref })}` : ''),
      );
      if (apiErr) setErrors(fieldErrors(apiErr));
    } finally {
      setSaving(false);
    }
  }

  function addFiles(list: FileList | null) {
    if (!list) return;
    const room = MAX_FILES - savedFiles.length - pending.length;
    const picked = [...list].slice(0, Math.max(room, 0));
    const tooBig = picked.find((f) => f.size > MAX_FILE_BYTES);
    if (tooBig) {
      setProblem(t('pr.error.file', { name: tooBig.name, reason: t('pr.error.too_large') }));
      return;
    }
    setPending([...pending, ...picked]);
  }

  const field = (
    id: string,
    label: MessageKey,
    control: JSX.Element,
    opts: { required?: boolean; hint?: string } = {},
  ) => {
    const err = message(id);
    return (
      <div class="field">
        <label for={`pr-${id}`}>
          {t(label)}
          {opts.required && <span aria-hidden="true"> *</span>}
        </label>
        {control}
        {opts.hint && (
          <p class="caption" id={`pr-${id}-hint`}>
            {opts.hint}
          </p>
        )}
        {err && (
          <p id={`pr-${id}-error`} class="field__error">
            {err}
          </p>
        )}
      </div>
    );
  };
  const aria = (id: string) => ({
    id: `pr-${id}`,
    'aria-invalid': errors[id] ? (true as const) : undefined,
    'aria-describedby': errors[id] ? `pr-${id}-error` : undefined,
  });

  const profileCard = (
    <section class="card pr-profile" aria-label={t('pr.profile.label')}>
      <div>
        <p class="pr-profile__name">{requester.name}</p>
        <p class="caption" dir="ltr">
          {requester.email}
        </p>
      </div>
      <dl class="pr-profile__facts">
        <div>
          <dt>{t('pr.profile.department')}</dt>
          <dd>{requester.department || '—'}</dd>
        </div>
        <div>
          <dt>{t('pr.profile.vertical')}</dt>
          <dd>{requester.vertical || '—'}</dd>
        </div>
        <div>
          <dt>{t('pr.profile.entity')}</dt>
          <dd dir="ltr">{requester.entity ?? '—'}</dd>
        </div>
      </dl>
      <p class="pr-profile__note caption">{t('pr.profile.note')}</p>
    </section>
  );

  if (!requester.entity) {
    return (
      <div class="stack">
        {profileCard}
        <section class="card card--error" role="alert">
          <h2 class="card__title">{t('pr.gate.title')}</h2>
          <p>{t('pr.gate.body')}</p>
        </section>
      </div>
    );
  }

  return (
    <div class="stack">
      {profileCard}
      <div class="pr-layout">
        <form
          class="card stack"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            void save(true);
          }}
        >
          {problem && (
            <p class="form-problem" role="alert">
              {problem}
            </p>
          )}
          {field(
            'title',
            'pr.f.title',
            <input
              {...aria('title')}
              type="text"
              maxLength={200}
              required
              value={form.title}
              onInput={(e) => set('title', (e.target as HTMLInputElement).value)}
            />,
            { required: true },
          )}
          <div class="admin-form__grid">
            {field(
              'chargedTo',
              'pr.f.chargedTo',
              <select
                {...aria('chargedTo')}
                aria-describedby={
                  errors.chargedTo ? 'pr-chargedTo-error pr-chargedTo-hint' : 'pr-chargedTo-hint'
                }
                value={chargedTo}
                onChange={(e) => set('chargedTo', (e.target as HTMLSelectElement).value)}
              >
                {!verticals.some((v) => v.vertical === chargedTo) && (
                  <option value={chargedTo}>{chargedTo}</option>
                )}
                {verticals.map((v) => (
                  <option key={v.vertical} value={v.vertical}>
                    {v.vertical}
                  </option>
                ))}
              </select>,
              { hint: t('pr.f.chargedToHint', { entity: chargedEntity }) },
            )}
            {field(
              'category',
              'pr.f.category',
              <select
                {...aria('category')}
                value={form.category}
                onChange={(e) => set('category', (e.target as HTMLSelectElement).value)}
              >
                <option value="">{t('pr.select')}</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {lang === 'ar' ? c.nameAR || c.nameEN : c.nameEN}
                  </option>
                ))}
              </select>,
              { required: true },
            )}
            {field(
              'neededBy',
              'pr.f.neededBy',
              <>
                <input
                  {...aria('neededBy')}
                  aria-describedby={
                    errors.neededBy ? 'pr-neededBy-error pr-neededBy-when' : 'pr-neededBy-when'
                  }
                  type="date"
                  dir="ltr"
                  value={form.neededBy}
                  onInput={(e) => set('neededBy', (e.target as HTMLInputElement).value)}
                />
                <DateHint id="pr-neededBy-when" value={form.neededBy} />
              </>,
              { required: true },
            )}
            {field(
              'location',
              'pr.f.location',
              <select
                {...aria('location')}
                value={form.location}
                onChange={(e) => set('location', (e.target as HTMLSelectElement).value)}
              >
                <option value="">{t('pr.select')}</option>
                {locations.map((l) => (
                  <option key={l.location} value={l.location}>
                    {l.location}
                  </option>
                ))}
              </select>,
              { required: true },
            )}
            {field(
              'currency',
              'pr.f.currency',
              <input id="pr-currency" type="text" dir="ltr" readOnly value={currency || '—'} />,
              { hint: t('pr.f.currencyHint') },
            )}
          </div>

          <fieldset class="pr-items">
            <legend>
              {t('pr.f.items')}
              <span aria-hidden="true"> *</span>
            </legend>
            {message('lines') && <p class="field__error">{message('lines')}</p>}
            {form.lines.map((line, i) => {
              const n = String(i + 1);
              const id = (k: string) => `lines.${i}.${k}`;
              return (
                <div
                  class="pr-line"
                  key={line.key}
                  role="group"
                  aria-label={t('pr.line.label', { n })}
                >
                  <div class="field pr-line__desc">
                    <label for={`pr-line-${line.key}-d`}>{t('pr.line.description')}</label>
                    <input
                      id={`pr-line-${line.key}-d`}
                      type="text"
                      maxLength={500}
                      aria-invalid={errors[id('description')] ? true : undefined}
                      value={line.description}
                      onInput={(e) =>
                        setLine(line.key, { description: (e.target as HTMLInputElement).value })
                      }
                    />
                    {message(id('description')) && (
                      <p class="field__error">{message(id('description'))}</p>
                    )}
                  </div>
                  <div class="field pr-line__specs">
                    <label for={`pr-line-${line.key}-s`}>{t('pr.line.specs')}</label>
                    <input
                      id={`pr-line-${line.key}-s`}
                      type="text"
                      maxLength={1000}
                      value={line.specs}
                      onInput={(e) =>
                        setLine(line.key, { specs: (e.target as HTMLInputElement).value })
                      }
                    />
                  </div>
                  <div class="field">
                    <label for={`pr-line-${line.key}-q`}>{t('pr.line.qty')}</label>
                    <input
                      id={`pr-line-${line.key}-q`}
                      type="number"
                      dir="ltr"
                      min="0"
                      step="any"
                      inputMode="decimal"
                      aria-invalid={errors[id('qty')] ? true : undefined}
                      value={line.qty}
                      onInput={(e) =>
                        setLine(line.key, { qty: (e.target as HTMLInputElement).value })
                      }
                    />
                    {message(id('qty')) && <p class="field__error">{message(id('qty'))}</p>}
                  </div>
                  <div class="field">
                    <label for={`pr-line-${line.key}-u`}>{t('pr.line.unit')}</label>
                    <select
                      id={`pr-line-${line.key}-u`}
                      value={line.unit}
                      onChange={(e) =>
                        setLine(line.key, { unit: (e.target as HTMLSelectElement).value as Unit })
                      }
                    >
                      {UNITS.map((u) => (
                        <option key={u} value={u}>
                          {t(`pr.unit.${u}` as MessageKey)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div class="field">
                    <label for={`pr-line-${line.key}-t`}>{t('pr.line.type')}</label>
                    <select
                      id={`pr-line-${line.key}-t`}
                      value={line.lineType}
                      onChange={(e) =>
                        setLine(line.key, {
                          lineType: (e.target as HTMLSelectElement).value as LineType,
                        })
                      }
                    >
                      {LINE_TYPES.map((lt) => (
                        <option key={lt} value={lt}>
                          {t(`pr.lineType.${lt}` as MessageKey)}
                        </option>
                      ))}
                    </select>
                  </div>
                  {form.lines.length > 1 && (
                    <button
                      type="button"
                      class="button button--small pr-line__remove"
                      aria-label={t('pr.line.remove', { n })}
                      onClick={() =>
                        set(
                          'lines',
                          form.lines.filter((l) => l.key !== line.key),
                        )
                      }
                    >
                      ×
                    </button>
                  )}
                </div>
              );
            })}
            {form.lines.length < MAX_LINES && (
              <button
                type="button"
                class="button"
                onClick={() => set('lines', [...form.lines, newLine()])}
              >
                + {t('pr.addItem')}
              </button>
            )}
          </fieldset>

          {field(
            'estBudget',
            'pr.f.budget',
            <input
              {...aria('estBudget')}
              type="number"
              dir="ltr"
              min="0"
              step="0.01"
              inputMode="decimal"
              value={form.estBudget}
              onInput={(e) => set('estBudget', (e.target as HTMLInputElement).value)}
            />,
            { hint: t('pr.noPrices') },
          )}
          {field(
            'justification',
            'pr.f.justification',
            <textarea
              {...aria('justification')}
              rows={4}
              maxLength={2000}
              value={form.justification}
              onInput={(e) => set('justification', (e.target as HTMLTextAreaElement).value)}
            />,
            { required: true },
          )}
          <div class="field field--check">
            <input
              id="pr-rush"
              type="checkbox"
              checked={form.rush}
              onChange={(e) => set('rush', (e.target as HTMLInputElement).checked)}
            />
            <label for="pr-rush">{t('pr.f.rush')}</label>
          </div>
          {form.rush &&
            field(
              'rushReason',
              'pr.f.rushReason',
              <textarea
                {...aria('rushReason')}
                rows={2}
                maxLength={1000}
                value={form.rushReason}
                onInput={(e) => set('rushReason', (e.target as HTMLTextAreaElement).value)}
              />,
              { required: true },
            )}

          <div class="field">
            <label for="pr-files">{t('pr.f.files')}</label>
            <input
              id="pr-files"
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx"
              aria-describedby="pr-files-hint"
              disabled={savedFiles.length + pending.length >= MAX_FILES}
              onChange={(e) => {
                const input = e.target as HTMLInputElement;
                addFiles(input.files);
                input.value = '';
              }}
            />
            <p class="caption" id="pr-files-hint">
              {t('pr.files.hint')}
            </p>
            {(savedFiles.length > 0 || pending.length > 0) && (
              <ul class="pr-files">
                {savedFiles.map((name) => (
                  <li key={`s-${name}`}>
                    <span dir="auto">{name}</span>{' '}
                    <span class="caption">{t('pr.files.saved')}</span>
                  </li>
                ))}
                {pending.map((file, i) => (
                  <li key={`p-${i}-${file.name}`}>
                    <span dir="auto">{file.name}</span>{' '}
                    <span class="caption">{t('pr.files.pending')}</span>
                    <button
                      type="button"
                      class="button button--small"
                      aria-label={t('pr.files.remove', { name: file.name })}
                      onClick={() => setPending(pending.filter((f) => f !== file))}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div class="actions">
            <button type="submit" class="button button--primary" disabled={saving}>
              {saving ? t('pr.saving') : t('pr.submit')}
            </button>
            <button type="button" class="button" disabled={saving} onClick={() => void save(false)}>
              {t('pr.saveDraft')}
            </button>
          </div>
        </form>

        <aside class="card pr-next" aria-labelledby="pr-next-title">
          <h2 id="pr-next-title" class="card__title">
            {t('pr.next.title')}
          </h2>
          <ol>
            {(['pr.next.1', 'pr.next.2', 'pr.next.3', 'pr.next.4'] as const).map((k) => (
              <li key={k}>{t(k)}</li>
            ))}
          </ol>
        </aside>
      </div>
    </div>
  );
}
